import { useState, useEffect, useRef, type RefObject } from 'react';

export function useCounter<T extends HTMLElement = HTMLElement>(
  target: number,
  duration = 1400,
): [number, RefObject<T | null>] {
  const [count, setCount] = useState(0);
  const ref = useRef<T>(null);
  const fired = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting !== true || fired.current) return;
        fired.current = true;
        observer.unobserve(el);

        const stepMs = duration / target;
        let current = 0;
        const interval = setInterval(() => {
          current += 1;
          setCount(current);
          if (current >= target) clearInterval(interval);
        }, stepMs);
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => { observer.disconnect(); };
  }, [target, duration]);

  return [count, ref] as [number, RefObject<T | null>];
}
